"use client";

import * as React from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import {
  $getRoot,
  $getSelection,
  $isElementNode,
  $isParagraphNode,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
  type LexicalEditor,
  type LexicalNode,
} from "lexical";
import { $isHeadingNode } from "@lexical/rich-text";
import { $isListItemNode, $isListNode } from "@lexical/list";
import {
  getHeadingLabelFromTag,
  LIST_ITEM_PLACEHOLDER_TEXT,
  PLACEHOLDER_COLOR_CLASS,
  PLACEHOLDER_TEXT,
} from "../constants";
import {
  LEXICAL_BODY_TYPOGRAPHY_CLASSES,
  LEXICAL_HEADING_TYPOGRAPHY_CLASSES,
} from "../composer/getLexicalTheme";
import { $ensureTailEmptyParagraphAndSelect } from "../helpers/ensureTailEmptyParagraph";

/** DOM focus 带 preventScroll，避免「点空白区补尾段」时浏览器把视口滚到选区 */
function focusEditorRootPreventScroll(editor: LexicalEditor): void {
  queueMicrotask(() => {
    const inner = editor.getRootElement();
    if (!inner) return;
    const ce = inner.closest(".rt-content-editable");
    const el = ce instanceof HTMLElement ? ce : inner;
    el.focus({ preventScroll: true });
  });
}

type PlaceholderOverlay = {
  nodeKey: string;
  top: number;
  left: number;
  height: number;
  text: string;
  contentClassName: string;
};

/** 选区 anchor 所在根级块（段落/标题等）的 node key；须在 read 内调用 */
function $topLevelBlockKeyContainingAnchor(): string | null {
  const sel = $getSelection();
  if (!$isRangeSelection(sel)) return null;
  const root = $getRoot();
  let n: LexicalNode | null = sel.anchor.getNode();
  while (n != null) {
    const parent: LexicalNode | null = n.getParent();
    if (parent === root) return n.getKey();
    n = parent;
  }
  return null;
}

function collectPlaceholderOverlays(
  editor: LexicalEditor,
  container: HTMLDivElement,
  editorDomFocused: boolean,
): PlaceholderOverlay[] {
  const next: PlaceholderOverlay[] = [];
  const seen = new Set<string>();
  const anchorBlockKey = $topLevelBlockKeyContainingAnchor();

  const pushOverlay = (
    nodeKey: string,
    text: string,
    contentClassName: string,
    minBlockHeight = 1,
  ) => {
    if (seen.has(nodeKey)) return;
    const element = editor.getElementByKey(nodeKey);
    if (!element) return;
    const cRect = container.getBoundingClientRect();
    const eRect = element.getBoundingClientRect();
    seen.add(nodeKey);
    next.push({
      nodeKey,
      top: eRect.top - cRect.top + container.scrollTop,
      left: eRect.left - cRect.left + container.scrollLeft + 2,
      height: Math.max(eRect.height, minBlockHeight),
      text,
      contentClassName,
    });
  };

  const root = $getRoot();
  for (const child of root.getChildren()) {
    if (!$isHeadingNode(child)) continue;
    if (child.getTextContent().trim().length !== 0) continue;
    const tag = child.getTag() as keyof typeof LEXICAL_HEADING_TYPOGRAPHY_CLASSES;
    const typo = LEXICAL_HEADING_TYPOGRAPHY_CLASSES[tag];
    if (!typo) continue;
    const label = getHeadingLabelFromTag(child.getTag()) ?? PLACEHOLDER_TEXT;
    pushOverlay(child.getKey(), label, `${typo} ${PLACEHOLDER_COLOR_CLASS}`);
  }

  const paragraphClass = `${LEXICAL_BODY_TYPOGRAPHY_CLASSES} ${PLACEHOLDER_COLOR_CLASS}`;
  const paragraphMinH = 28;
  for (const child of root.getChildren()) {
    if (!$isParagraphNode(child)) continue;
    if (child.getTextContent().trim().length !== 0) continue;
    if (!editorDomFocused || anchorBlockKey !== child.getKey()) continue;
    pushOverlay(child.getKey(), PLACEHOLDER_TEXT, paragraphClass, paragraphMinH);
  }

  const listItemClass = `${LEXICAL_BODY_TYPOGRAPHY_CLASSES} ${PLACEHOLDER_COLOR_CLASS}`;
  const walkList = (node: LexicalNode) => {
    if ($isListItemNode(node)) {
      if (node.getTextContent().trim().length === 0) {
        pushOverlay(node.getKey(), LIST_ITEM_PLACEHOLDER_TEXT, listItemClass, paragraphMinH);
      }
    }
    const children = $isElementNode(node) ? node.getChildren() : [];
    for (const ch of children) {
      if ($isListNode(ch) || $isListItemNode(ch)) walkList(ch);
    }
  };
  for (const child of root.getChildren()) {
    if ($isListNode(child) || $isListItemNode(child)) walkList(child);
  }

  return next;
}

export function EmptyParagraphPlaceholderPlugin({
  isEditable,
  containerRef,
}: {
  isEditable: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [editor] = useLexicalComposerContext();
  const [overlays, setOverlays] = React.useState<PlaceholderOverlay[]>([]);
  const [editorDomFocused, setEditorDomFocused] = React.useState(false);

  const measure = React.useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      setOverlays([]);
      return;
    }
    editor.getEditorState().read(() => {
      setOverlays(collectPlaceholderOverlays(editor, container, editorDomFocused));
    });
  }, [editor, containerRef, editorDomFocused]);

  const scheduleMeasure = React.useCallback(() => {
    measure();
    requestAnimationFrame(() => {
      measure();
      requestAnimationFrame(() => measure());
    });
  }, [measure]);

  React.useLayoutEffect(() => {
    if (!isEditable) return;
    scheduleMeasure();
  }, [isEditable, scheduleMeasure]);

  React.useEffect(() => {
    if (!isEditable) return;
    const container = containerRef.current;
    if (!container) return;
    const onScroll = () => scheduleMeasure();
    container.addEventListener("scroll", onScroll, { passive: true });
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => scheduleMeasure());
      ro.observe(container);
    }
    return () => {
      container.removeEventListener("scroll", onScroll);
      ro?.disconnect();
    };
  }, [isEditable, containerRef, scheduleMeasure]);

  /** 焦点在编辑器容器内（含正文）时为 true，用于空段落 placeholder 显隐 */
  React.useEffect(() => {
    if (!isEditable) return;
    const c = containerRef.current;
    if (!c) return;
    const sync = () => {
      setEditorDomFocused(typeof document !== "undefined" && c.contains(document.activeElement));
    };
    sync();
    c.addEventListener("focusin", sync, true);
    c.addEventListener("focusout", sync, true);
    return () => {
      c.removeEventListener("focusin", sync, true);
      c.removeEventListener("focusout", sync, true);
    };
  }, [isEditable, containerRef]);

  React.useEffect(() => {
    if (!isEditable) return;
    scheduleMeasure();
  }, [editorDomFocused, isEditable, scheduleMeasure]);

  React.useEffect(() => {
    if (!isEditable) return;
    const container = containerRef.current;
    if (!container) return;

    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;

      if (t.closest(".rt-content-editable")) return;

      if (t.closest('[data-block-drag-handle="true"]')) return;
      if (t.closest('[data-floating-root="true"]')) return;
      if (t.closest("button, select, input, textarea")) return;

      // 外层 p-12：仅当点击落在正文区「正下方」的纵向 padding 内才聚焦尾部（与横轴对齐）。
      // 上/左/右内边距误触会执行 focus 尾段 → 浏览器滚到文档底，违背正常滚动预期。
      const contentEl = container.querySelector(".rt-content-editable");
      if (contentEl instanceof HTMLElement) {
        const cr = contentEl.getBoundingClientRect();
        if (e.clientY < cr.top || e.clientX < cr.left || e.clientX > cr.right) return;
      }

      editor.update(() => {
        $ensureTailEmptyParagraphAndSelect();
      });
      focusEditorRootPreventScroll(editor);
    };

    container.addEventListener("pointerdown", onPointerDown, true);
    return () => container.removeEventListener("pointerdown", onPointerDown, true);
  }, [editor, isEditable, containerRef]);

  React.useEffect(() => {
    if (!isEditable) return;

    const onTailPointerDownCapture = (e: PointerEvent) => {
      if (e.button !== 0) return;
      const rootEl = editor.getRootElement();
      if (!rootEl) return;
      const t = e.target;
      if (!(t instanceof Node) || !rootEl.contains(t)) return;
      if (t instanceof Element) {
        if (t.closest("a[href]")) return;
        if (t.closest("[data-image-block]")) return;
        if (t.closest("table")) return;
      }

      const children = rootEl.children;
      const last = children.length ? (children[children.length - 1] as HTMLElement) : null;

      const clickBelowLast = (): boolean => {
        if (!last) return true;
        const br = last.getBoundingClientRect();
        return e.clientY > br.bottom + 1;
      };

      // 勿用 e.target === rootEl：首块上方的根内空白也会命中 root，误触尾段逻辑会滚到底部。
      if (!clickBelowLast()) return;

      const skipEnsure = editor.getEditorState().read(() => {
        const root = $getRoot();
        const kids = root.getChildren();
        const lexicalLast = kids.length ? kids[kids.length - 1] : null;
        if (
          !lexicalLast ||
          !$isParagraphNode(lexicalLast) ||
          lexicalLast.getTextContent().trim() !== ""
        ) {
          return false;
        }
        const lastKey = lexicalLast.getKey();
        const sel = $getSelection();
        if (!$isRangeSelection(sel)) return false;
        let n: LexicalNode | null = sel.anchor.getNode();
        while (n) {
          if (n.getKey() === lastKey) return true;
          n = n.getParent();
        }
        return false;
      });

      if (!skipEnsure) {
        editor.update(() => {
          $ensureTailEmptyParagraphAndSelect();
        });
      }
      focusEditorRootPreventScroll(editor);
    };

    let raf = 0;
    return mergeRegister(
      editor.registerRootListener((rootEl, prevRoot) => {
        if (prevRoot) {
          prevRoot.removeEventListener("pointerdown", onTailPointerDownCapture, true);
        }
        if (rootEl) {
          rootEl.addEventListener("pointerdown", onTailPointerDownCapture, true);
          scheduleMeasure();
        }
      }),
      editor.registerUpdateListener(() => {
        if (raf) return;
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          scheduleMeasure();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          scheduleMeasure();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, isEditable, scheduleMeasure]);

  if (!isEditable) return null;

  return (
    <div
      className="pointer-events-none absolute z-10"
      style={{
        inset: 0,
        display: overlays.length ? "block" : "none",
      }}
    >
      {overlays.map((overlay) => (
        <div
          key={overlay.nodeKey}
          className="flex items-center"
          style={{
            position: "absolute",
            top: overlay.top,
            left: overlay.left,
            height: overlay.height,
            minHeight: overlay.height,
          }}
        >
          <span className={overlay.contentClassName}>{overlay.text}</span>
        </div>
      ))}
    </div>
  );
}
